import { onlineManager, QueryClient } from "@tanstack/react-query"
import { Refused } from "@/administration/refused.ts"

/** How many times a call that failed before the web API could answer is made again. */
const retriesAfterAFault = 2

/**
 * Whether to ask again after a failed call. A refusal is the web API's decision, so asking again
 * can only get the same answer; a failure that never reached it, such as a dropped connection, may
 * pass on a second try.
 */
export function shouldRetry(failureCount: number, error: unknown) {
  return !(error instanceof Refused) && failureCount < retriesAfterAFault
}

/**
 * The web app's one cache of what the web API answered. A query is asked again whenever the window
 * regains focus and whenever a page that shows it mounts, so what a Member sees is never older than
 * their last look away; a page that must stay fresh while open polls on top of that. While the
 * browser is offline a query waits rather than failing, and is asked as the network returns.
 */
export function createQueryClient() {
  // The cache takes itself to be online until the browser says otherwise, which a page opened
  // offline (the installed app's shell, from its service worker) never hears it say.
  onlineManager.setOnline(navigator.onLine)
  return new QueryClient({
    defaultOptions: {
      queries: { retry: shouldRetry, refetchOnWindowFocus: true },
      mutations: { retry: false },
    },
  })
}
